import React from 'react';
import styled, { css } from 'styled-components';

import { Bubble } from '@lt/components';
import { useTranslation } from '@lt/localization';
import FlightDuration from './FlightDuration/FlightDuration';
import { formatDateToDay } from '../../../../../../utils/dateUtils';
import { getDateWithoutTimezone, getHoursSafe } from '../../utils/flightUtils';
import FlightsLuggage from './Luggage/FlightsLuggage/FlightsLuggage';
import type { FlightsData } from '../../types/flights';
import FlightTransferHint from './FlightTransferHint';

const baseCls = 'checkout-flight-route';

interface FlightsRouteProps {
  data: FlightsData['to'];
  type: 'to' | 'back';
  isAdditional?: boolean;
  isMobile?: boolean;
  showLogo?: boolean;
}

const FlightsRoute = ({
  data,
  type,
  showLogo,
  isAdditional,
  isMobile,
}: FlightsRouteProps) => {
  const { t } = useTranslation();
  const {
    arrival,
    departure,
    origin,
    destination,
    time,
    airline,
    transition,
    transition_time,
    direct,
    luggage,
  } = data;

  const parseDeparture = departure ? getDateWithoutTimezone(departure) : null;
  const parseArrival = arrival ? getDateWithoutTimezone(arrival) : null;

  return (
    <StyledMainContainer $isDirectionTo={type === 'to'}>
      <StyleFlightInfo>
        <FlightsLuggage
          className={`${baseCls}__luggage`}
          luggage={luggage}
          isMobile={isMobile}
        />
      </StyleFlightInfo>

      <StyledWrapper>
        <StyledContainer $isMobile={isMobile}>
          <StyledRow>
            <StyledDeparture>
              <StyledTime $isAdditional={isAdditional}>
                {!!time && parseDeparture
                  ? getHoursSafe(parseDeparture)
                  : t('checkout.flight.emptytime.noData')}
              </StyledTime>

              <StyledDate $isAdditional={isAdditional}>
                {parseDeparture
                  ? formatDateToDay(parseDeparture)
                  : t('checkout.flight.emptytime.full_noData')}
              </StyledDate>
              <StyledAirport
                $isMobile={isMobile}
                title={origin?.name}
                $isAdditional={isAdditional}
              >
                {origin && origin.name
                  ? origin.name
                  : t('checkout.flight.emptytime.full_noData')}
              </StyledAirport>
            </StyledDeparture>
            <StyledFlightsDurationContainer
              $isDirectionTo={type === 'to'}
              $isMobile={isMobile}
            >
              <StyledBubble
                placement="top"
                text=" "
                disabled={direct}
                renderExtras={() => (
                  <FlightTransferHint
                    transition={transition}
                    transition_time={transition_time}
                  />
                )}
              >
                <FlightDuration
                  value={time}
                  direct={direct}
                  isMobile={!!isMobile}
                />
              </StyledBubble>
            </StyledFlightsDurationContainer>
          </StyledRow>
        </StyledContainer>
        <StyledArrival $isMobile={isMobile}>
          <StyledTimeMedium
            $isAdditional={isAdditional}
            $needMargin={!isMobile}
          >
            {!!time && parseArrival
              ? getHoursSafe(parseArrival)
              : t('checkout.flight.emptytime.noData')}
          </StyledTimeMedium>
          <StyledDate $isAdditional={isAdditional}>
            {parseArrival
              ? formatDateToDay(parseArrival)
              : t('checkout.flight.emptytime.full_noData')}
          </StyledDate>
          <StyledAirport
            $isMobile={isMobile}
            title={destination?.name}
            $isAdditional={isAdditional}
          >
            {destination && destination.name
              ? destination.name
              : t('checkout.flight.emptytime.full_noData')}
          </StyledAirport>
        </StyledArrival>

        {showLogo &&
          (airline ? (
            <StyledLogo>
              <StyledImg
                src={airline.logo.src}
                alt={airline.name}
                title={airline.name}
              />
            </StyledLogo>
          ) : (
            <StyledUnknownAirline>
              <StyledSquare />
              <StyledUnknownText $isMobile={isMobile}>
                {t('checkout.flight.emptyAirline')}
              </StyledUnknownText>
            </StyledUnknownAirline>
          ))}
      </StyledWrapper>
    </StyledMainContainer>
  );
};

const StyleFlightInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const StyledMainContainer = styled.div<{ $isDirectionTo: boolean }>`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  ${({ $isDirectionTo }) => $isDirectionTo && `margin-top: 18px;`}
`;

const StyledDeparture = styled.div`
  width: min-content;
`;

const StyledUnknownAirline = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #919499;
  line-height: 16px;
`;

const StyledUnknownText = styled.span<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile ? `margin-left: 8px;` : `  position: absolute;`}
`;

const StyledSquare = styled.div`
  width: 105px;
  height: 70px;

  border-radius: 4px;
  border: solid 1px;
  border-color: #e6e9ee;
`;

const StyledArrival = styled.div<{ $isMobile?: boolean }>`
  position: relative;
  flex-grow: 1;
  min-width: 75px;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      position: relative;
      width: auto;
      min-width: 0;
      overflow: visible;
      width: auto;
    `}
`;

const StyledTimeMedium = styled.div<{
  $needMargin: boolean;
  $isAdditional?: boolean;
  $isMobile?: boolean;
}>`
  font-size: 18px;
  color: var(--black);
  font-style: normal;
  font-weight: 500;
  ${({ $needMargin }) =>
    $needMargin &&
    css`
      margin-top: 10px;
    `}
  ${({ $isAdditional }) =>
    $isAdditional &&
    css`
      color: var(--white);
    `}
`;

const StyledWrapper = styled.div`
  padding-top: 0;
  display: flex;
  min-width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledContainer = styled.div<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
      min-width: auto;
      max-width: auto;
      width: auto;`
      : `
      min-width: 235px;
      max-width: 315px;
      width: 100%;
  `}
`;

const StyledTime = styled.div<{
  $isAdditional?: boolean;
}>`
  margin-top: 0;
  color: var(--black);
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  ${({ $isAdditional }) =>
    $isAdditional &&
    css`
      color: var(--white);
    `}
`;

const StyledDate = styled.div<{ $isAdditional?: boolean }>`
  white-space: nowrap;
  color: var(--black);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  ${({ $isAdditional }) =>
    $isAdditional &&
    css`
      color: var(--white);
    `}
`;

const StyledAirport = styled.div<{
  $isMobile?: boolean;
  $isAdditional?: boolean;
}>`
  font-style: normal;
  font-weight: 400;
  ${({ $isMobile }) =>
    $isMobile
      ? css`
          width: 100%;
          font-size: 12px;
        `
      : css`
          width: auto;
          font-size: 14px;
        `};
  ${({ $isAdditional }) =>
    $isAdditional &&
    css`
      color: var(--white);
    `}
`;

const StyledLogo = styled.div`
  margin-left: 10px;
  display: flex;
  max-height: 70px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: 100px;
`;

const StyledImg = styled.img`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const StyledFlightsDurationContainer = styled.div<{
  $isDirectionTo: boolean;
  $isMobile?: boolean;
}>`
  ${({ $isMobile }) =>
    $isMobile &&
    `
      margin-top: -11px;
      margin-right: 10px;
      margin-left: -10px;
      padding: 0;
      width: auto;
      min-width: 100px;
  `}
  margin-right: 10px;
  margin-left: 5px;
  width: 100%;
  min-width: 140px;
  max-width: 220px;
  flex-shrink: 10;
  text-align: center;
  display: block;

  ${({ $isDirectionTo }) => $isDirectionTo && `padding-top: 5px;`}
`;

const StyledBubble = styled(Bubble)`
  display: block;
`;

export default FlightsRoute;
